import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Wrench, LayoutDashboard, Columns3, PlusCircle, Settings, LogOut, KeyRound, ClipboardList, Menu } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from './ui/dropdown-menu';
import { useAuth } from '../lib/AuthContext';
import { api, errMsg } from '../lib/api';
import { SubscriptionBanner } from './SubscriptionBanner';

const NAV_BY_ROLE = {
  owner: [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/board', label: 'Board', icon: Columns3 },
    { to: '/intake', label: 'Intake', icon: PlusCircle },
    { to: '/settings', label: 'Settings', icon: Settings },
  ],
  advisor: [
    { to: '/board', label: 'Board', icon: Columns3 },
    { to: '/intake', label: 'Intake', icon: PlusCircle },
  ],
  technician: [
    { to: '/tech', label: 'My jobs', icon: ClipboardList },
  ],
};

function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      toast.success('Password updated');
      onOpenChange(false);
      setCurrent('');
      setNext('');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-2xl">
        <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="change-password-current-input" type="password" required placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} className="h-11 rounded-xl" />
          <Input data-testid="change-password-new-input" type="password" required minLength={8} placeholder="New password (min 8 characters)" value={next} onChange={(e) => setNext(e.target.value)} className="h-11 rounded-xl" />
          <DialogFooter>
            <Button data-testid="change-password-submit-button" type="submit" disabled={busy} className="w-full h-11 rounded-xl">
              {busy ? 'Saving…' : 'Update password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export const Layout = ({ children }) => {
  const { me, config, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const role = me?.user?.role;
  const nav = NAV_BY_ROLE[role] || [];

  return (
    <div className="min-h-dvh bg-[hsl(var(--background))]">
      <header className="sticky top-0 z-40 bg-[hsl(var(--background))]/95 backdrop-blur border-b border-[hsl(var(--border))]">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-14">
            <button data-testid="nav-logo" onClick={() => navigate('/')} className="flex items-center gap-2 font-display font-semibold">
              <span className="h-8 w-8 rounded-lg bg-[hsl(var(--primary))] text-white flex items-center justify-center">
                <Wrench className="h-4 w-4" />
              </span>
              <span className="hidden sm:inline">{config?.app_name || 'Auto Pampanga'}</span>
            </button>
            <nav className="flex items-center gap-1">
              {nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 rounded-xl px-2.5 py-2 text-sm font-medium transition-colors ${
                      isActive ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]' : 'text-[hsl(var(--muted-foreground))] hover:bg-black/5'
                    }`
                  }
                >
                  <item.icon className="h-4 w-4" />
                  <span className="hidden min-[420px]:inline">{item.label}</span>
                </NavLink>
              ))}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="nav-user-menu-button" variant="ghost" size="icon" className="rounded-xl" aria-label="Account menu">
                    <Menu className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl">
                  <DropdownMenuLabel className="text-xs">
                    <div className="font-medium text-sm">{me?.user?.name}</div>
                    <div className="text-[hsl(var(--muted-foreground))] capitalize">{role} · {me?.shop?.name}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="nav-change-password" onClick={() => setPwOpen(true)}>
                    <KeyRound className="h-4 w-4 mr-2" /> Change password
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="nav-logout" onClick={() => { logout(); navigate('/login'); }}>
                    <LogOut className="h-4 w-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-4 md:px-6 md:py-6 space-y-4">
        <SubscriptionBanner />
        {children}
      </main>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
};
